// The conversation: every email sent or received by time, a reply in green with the AI's reading (the User can
// change it), and the reply box: Suggest reply (AI), Send reply, Not a hot lead, Snooze 3 days.
import { Loader2, Send, Sparkles } from "lucide-react";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState, type ReactNode } from "react";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  buildReplyPrompt,
  ReplySchema,
} from "../../../../../supabase/functions/_shared/leadAgent.ts";
import type { CrmDataProvider } from "../../providers/types";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import { bookingLine } from "../../settings/outreachSettings";
import { runAiJson } from "../ai";
import {
  INTENTS,
  type Intent,
  type PreLead,
  type PreLeadMessage,
} from "./types";
import { useDrawerTask } from "./useDrawerTask";

const SNOOZE_DAYS = 3;
/** labels that close the pre-lead and suppress the address: asked before they are sent */
const CLOSING_INTENTS: Intent[] = ["not_interested", "unsubscribe"];

const timeOf = (m: PreLeadMessage) =>
  m.received_at ?? m.sent_at ?? m.scheduled_for ?? m.created_at;

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const IntentSelect = ({
  message,
  disabled,
  onChange,
}: {
  message: PreLeadMessage;
  disabled: boolean;
  onChange: (intent: Intent) => void;
}) => {
  const translate = useTranslate();
  return (
    <select
      aria-label={translate("resources.pre_leads.drawer.change_intent")}
      className="h-7 rounded-md border bg-transparent px-1 text-xs"
      value={message.intent ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as Intent)}
    >
      {!message.intent && <option value="">—</option>}
      {INTENTS.map((intent) => (
        <option key={intent} value={intent}>
          {translate(`resources.notes.intent.values.${intent}`)}
        </option>
      ))}
    </select>
  );
};

const Email = ({
  message,
  children,
}: {
  message: PreLeadMessage;
  children?: ReactNode;
}) => {
  const translate = useTranslate();
  const inbound = message.direction === "in";
  return (
    <article
      className={cn(
        "rounded-md border p-3 text-sm",
        inbound &&
          "border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/40",
      )}
    >
      <header className="mb-1 flex flex-wrap items-baseline justify-between gap-x-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {inbound
            ? `${message.from_name || message.from_email} → ${message.mailbox ?? ""}`
            : `${message.from_email ?? message.mailbox ?? ""} → ${message.to_email ?? ""}`}
        </span>
        <span>
          {when(timeOf(message))}
          {message.touch
            ? ` · ${translate("resources.pre_leads.drawer.touch", { touch: message.touch })}`
            : ""}
        </span>
      </header>
      {message.subject && <p className="font-medium">{message.subject}</p>}
      <p className="whitespace-pre-wrap">{message.body}</p>
      {message.attached?.length ? (
        <p className="mt-1 text-xs text-muted-foreground">
          {message.attached.join(", ")}
        </p>
      ) : null}
      {children}
    </article>
  );
};

export const ThreadTab = ({
  preLead,
  messages,
  wide,
}: {
  preLead: PreLead;
  messages: PreLeadMessage[];
  wide: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { lead_brochures: brochures = [], booking_url } =
    useConfigurationContext();
  const { running, run } = useDrawerTask();
  const [reply, setReply] = useState("");
  // the relabel waiting for a confirm; the select stays on the saved intent until it is sent
  const [relabel, setRelabel] = useState<{
    id: Identifier;
    intent: Intent;
  } | null>(null);
  const t = (key: string, args?: Record<string, unknown>) =>
    translate(`resources.pre_leads.drawer.${key}`, args);

  // drafts live in the Draft tab; the thread is what was sent or received
  const thread = messages
    .filter((m) => m.status !== "draft")
    .sort((a, b) => timeOf(a).localeCompare(timeOf(b)));
  const lastIn = [...thread].reverse().find((m) => m.direction === "in");
  // the server replies from the mailbox the prospect wrote to
  const mailbox = lastIn?.mailbox ?? preLead.mailbox ?? "";

  const reclassify = (id: Identifier, intent: Intent) =>
    run(`intent-${id}`, () =>
      dataProvider.outreach({ action: "reclassify", message_id: id, intent }),
    );

  const suggest = () =>
    run("suggest", async () => {
      const draft = await runAiJson(
        "draft_reply",
        buildReplyPrompt({
          thread: thread.map((m) => ({
            subject: m.subject ?? "",
            body: m.body ?? "",
          })),
          brochures,
          booking_line: bookingLine(lastIn?.intent, booking_url),
        }),
        ReplySchema,
      );
      setReply(draft.body);
    });

  const send = () =>
    run("send", async () => {
      await dataProvider.outreach({
        action: "reply",
        pre_lead_id: preLead.id,
        body: reply,
        mailbox,
      });
      setReply("");
      notify("resources.pre_leads.drawer.notify.reply_sent", {
        type: "success",
      });
    });

  const update = (name: string, data: Partial<PreLead>, done: string) =>
    run(name, async () => {
      await dataProvider.update("pre_leads", {
        id: preLead.id,
        data,
        previousData: preLead,
      });
      notify(done, { type: "info" });
    });

  const busy = running != null;

  return (
    <div
      className={cn(
        "grid gap-4",
        wide && lastIn && "grid-cols-[minmax(0,1fr)_minmax(0,1fr)]",
      )}
    >
      <div className="flex flex-col gap-2">
        {thread.length ? (
          thread.map((m) => (
            <Email key={m.id} message={m}>
              {m.direction === "in" && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>
                    {m.intent
                      ? t("ai_read", {
                          intent: translate(
                            `resources.notes.intent.values.${m.intent}`,
                          ),
                        })
                      : null}
                    {m.intent_quote && `: “${m.intent_quote}”`}
                  </span>
                  <IntentSelect
                    message={m}
                    disabled={busy}
                    onChange={(intent) =>
                      CLOSING_INTENTS.includes(intent)
                        ? setRelabel({ id: m.id, intent })
                        : reclassify(m.id, intent)
                    }
                  />
                </div>
              )}
            </Email>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">{t("no_thread")}</p>
        )}
      </div>
      {lastIn && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">
            {t("reply_from", { mailbox })}
          </p>
          <Textarea
            aria-label={t("reply")}
            className="min-h-40"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy || !reply.trim()} onClick={send}>
              {running === "send" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Send />
              )}
              {t("send_reply")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={suggest}
            >
              {running === "suggest" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Sparkles />
              )}
              {t("suggest")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                update(
                  "not_hot",
                  {
                    status: "closed",
                    closed_reason: "not_hot",
                    needs_reply: false,
                    closed_at: new Date().toISOString(),
                  },
                  "resources.pre_leads.drawer.notify.not_hot",
                )
              }
            >
              {t("not_hot")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() =>
                update(
                  "snooze",
                  {
                    snoozed_until: new Date(
                      Date.now() + SNOOZE_DAYS * 86_400_000,
                    ).toISOString(),
                    needs_reply: false,
                  },
                  "resources.pre_leads.drawer.notify.snoozed",
                )
              }
            >
              {t("snooze")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t("convert_help")}</p>
        </div>
      )}
      <Confirm
        isOpen={relabel != null}
        title="resources.pre_leads.drawer.confirm_intent"
        content="resources.pre_leads.drawer.confirm_intent_help"
        titleTranslateOptions={{
          intent: relabel
            ? translate(`resources.notes.intent.values.${relabel.intent}`)
            : "",
        }}
        confirmColor="warning"
        onClose={() => setRelabel(null)}
        onConfirm={() => {
          if (relabel) reclassify(relabel.id, relabel.intent);
          setRelabel(null);
        }}
      />
    </div>
  );
};
